<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { getLanguageDisplayName } from 'dashboard/helper/languageFlags';

import FormSelect from 'v3/components/Form/Select.vue';

defineProps({
  label: { type: String, default: '' },
  description: { type: String, default: '' },
});

const { t, locale } = useI18n();
const { updateUISettings, uiSettings } = useUISettings();
const { enabledLanguages } = useConfig();

const languageOptions = computed(() => [...(enabledLanguages ?? [])]);

const updateLanguage = async languageCode => {
  try {
    const valid = (enabledLanguages || []).some(
      l => l.iso_639_1_code === languageCode
    );
    if (!valid) {
      throw new Error(`Invalid language code: ${languageCode}`);
    }

    await updateUISettings({ locale: languageCode });
    locale.value = languageCode;

    useAlert(
      t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.UPDATE_SUCCESS')
    );
  } catch {
    useAlert(
      t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.UPDATE_ERROR')
    );
  }
};

const selectedValue = computed({
  get: () => uiSettings.value?.locale || 'en',
  set: value => {
    updateLanguage(value);
  },
});
</script>

<template>
  <div class="flex flex-col gap-1 w-full">
    <label class="text-n-gray-12 font-medium text-sm">{{ label }}</label>
    <p v-if="description" class="mb-0 text-n-gray-11 text-xs">
      {{ description }}
    </p>
    <FormSelect
      v-model="selectedValue"
      name="language"
      spacing="compact"
      class="w-full"
      :options="languageOptions"
      label=""
    >
      <option
        v-for="option in languageOptions"
        :key="option.iso_639_1_code"
        :value="option.iso_639_1_code"
        :selected="option.iso_639_1_code === selectedValue"
      >
        {{ getLanguageDisplayName(option.iso_639_1_code, option.name) }}
      </option>
    </FormSelect>
  </div>
</template>
